// ─────────────────────────────────────────────────────────────
// Payload helpers — everything that converts between Superset
// API shapes, canvas cards, and the exported layout JSON.
// ─────────────────────────────────────────────────────────────
import SUPERSET_CANVAS_CONFIG from '../superset.canvas.config';
import { DEFAULT_CARD_OPTIONS, normalizeCardOptions } from './cardOptions';
import { getColor, getIconColor, getIconKey, mapVizType, safeParse } from './vizMapping';

const { GRID, CARD_DEFAULTS, EXPORT_VERSION } = SUPERSET_CANVAS_CONFIG;

export const snapToGrid = value => Math.round(value / GRID) * GRID;

export const makeCardId = () => `card-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

// ── Superset chart detail → POST /api/v1/chart/data body ──
// Superset stores the exact query the chart runs in `query_context`.
// Replaying it verbatim is what keeps the canvas preview identical
// to the chart as saved in Superset.
export function buildChartDataPayload(detail) {
  const queryContext = safeParse(detail?.query_context);
  if (!queryContext?.datasource) return null;

  const query = queryContext.queries?.[0] || {};

  return {
    datasource: queryContext.datasource,
    force: queryContext.force ?? false,
    queries: [
      {
        ...query,
        filters: query.filters || [],
        columns: query.columns || [],
        metrics: query.metrics || [],
        annotation_layers: query.annotation_layers || [],
        url_params: query.url_params || {},
        custom_params: query.custom_params || {},
        custom_form_data: query.custom_form_data || {},
      },
    ],
    result_format: queryContext.result_format || 'json',
    result_type: queryContext.result_type || 'full',
  };
}

// ── (chart detail, /chart/data result) → card liveData ──
export function buildLiveData(detail, queryResult, queryBody) {
  const result = queryResult?.result?.[0] ?? {};
  return {
    rows: result.data ?? [],
    columns: result.colnames ?? [],
    coltypes: result.coltypes ?? [],
    rowcount: result.rowcount ?? result.data?.length ?? 0,

    chart_id: detail?.id,
    slice_name: detail?.slice_name,
    viz_type: detail?.viz_type,
    description: detail?.description,

    datasource_id: detail?.datasource_id,
    datasource_type: detail?.datasource_type,
    datasource_name: detail?.datasource_name_text,

    form_data: safeParse(detail?.params),
    _query_context: queryBody ?? null,

    owners: detail?.owners,
    changed_on: detail?.changed_on_humanized,
  };
}

// ── cards → export payload (the contract other systems consume) ──
export function buildExportPayload(cards = [], options = {}) {
  const { gridPx = GRID, supersetUrl = null, liveDataByCard = {}, meta = {} } = options;

  const canvasWidth = cards.length ? Math.max(...cards.map(c => c.x + c.w)) : 0;
  const canvasHeight = cards.length ? Math.max(...cards.map(c => c.y + c.h)) : 0;

  return {
    version: EXPORT_VERSION,
    exported_at: new Date().toISOString(),
    grid_px: gridPx,
    canvas_width: canvasWidth,
    canvas_height: canvasHeight,
    // Token is deliberately never exported — the viewer re-authenticates.
    superset_url: supersetUrl,
    ...meta,

    charts: cards.map((card, idx) => {
      const chart = card.chart ?? {};
      const fd = chart.params ?? {};
      const live = liveDataByCard[card.id]?.data ?? null;

      return {
        order: idx,
        card_id: card.id,
        chart_id: chart.id ?? null,
        slice_id: chart.slice_id ?? null,
        slice_name: chart.slice_name,
        description: chart.description ?? null,

        viz_type: chart.viz_type,
        draw: chart.draw,
        icon: chart.icon ?? null,
        color: chart.color ?? null,
        ic: chart.ic ?? null,

        datasource_id: chart.datasource_id ?? null,
        datasource_type: chart.datasource_type ?? null,
        datasource_name: chart.datasource_name ?? null,

        form_data: {
          slice_id: chart.id ?? null,
          viz_type: chart.viz_type,
          datasource: chart.datasource_id ? `${chart.datasource_id}__${chart.datasource_type}` : null,
          metrics: fd.metrics ?? [],
          groupby: fd.groupby ?? [],
          columns: fd.columns ?? [],
          time_range: fd.time_range ?? 'No filter',
          granularity_sqla: fd.granularity_sqla ?? null,
          time_grain_sqla: fd.time_grain_sqla ?? null,
          adhoc_filters: fd.adhoc_filters ?? [],
          x_axis: fd.x_axis ?? null,
          x_axis_label: fd.x_axis_label ?? null,
          y_axis_label: fd.y_axis_label ?? null,
          color_scheme: fd.color_scheme ?? null,
          show_legend: fd.show_legend ?? null,
          row_limit: fd.row_limit ?? null,
          ...fd,
        },

        // Exact POST body for /api/v1/chart/data — lets the viewer replay
        // the same query without hitting /chart/:id first.
        query_context: live?._query_context ?? chart._query_context ?? null,

        // Presentation options travel with the layout so the viewer
        // reproduces the customised card exactly.
        options: normalizeCardOptions(card.options),

        layout: {
          x: Math.round(card.x),
          y: Math.round(card.y),
          w: Math.round(card.w),
          h: Math.round(card.h),
        },

        grid: {
          col: Math.round(card.x / gridPx),
          row: Math.round(card.y / gridPx),
          col_span: Math.round(card.w / gridPx),
          row_span: Math.round(card.h / gridPx),
        },

        live_data_snapshot: live
          ? {
              rowcount: live.rowcount,
              columns: live.columns,
              coltypes: live.coltypes,
              sample_rows: live.rows?.slice(0, 5) ?? [],
            }
          : null,
      };
    }),
  };
}

// ── export payload → cards (re-opening a saved layout in the builder) ──
export function payloadToCards(payload) {
  if (!payload?.charts?.length) return [];
  return payload.charts.map(entry => ({
    id: entry.card_id || makeCardId(),
    x: entry.layout?.x ?? 0,
    y: entry.layout?.y ?? 0,
    w: entry.layout?.w ?? CARD_DEFAULTS.W,
    h: entry.layout?.h ?? CARD_DEFAULTS.H,
    options: entry.options ? normalizeCardOptions(entry.options) : DEFAULT_CARD_OPTIONS,
    chart: {
      id: entry.chart_id,
      slice_id: entry.slice_id,
      slice_name: entry.slice_name,
      viz_type: entry.viz_type,
      draw: entry.draw || mapVizType(entry.viz_type),
      icon: entry.icon || getIconKey(entry.viz_type),
      color: entry.color || getColor(entry.viz_type),
      ic: entry.ic || getIconColor(entry.viz_type),
      datasource_id: entry.datasource_id,
      datasource_type: entry.datasource_type,
      datasource_name: entry.datasource_name,
      params: entry.form_data ?? {},
      description: entry.description,
      _query_context: entry.query_context ?? null,
    },
  }));
}

export function validateLayoutPayload(payload) {
  if (!payload || typeof payload !== 'object') throw new Error('Payload must be a JSON object');
  if (!Array.isArray(payload.charts)) throw new Error('Missing "charts" array — is this a valid dashboard export?');
  if (!payload.charts.length) throw new Error('No charts in this export');
  return payload;
}

// Small local download — avoids pulling file-saver in for one Blob.
export function downloadJson(data, filename) {
  const blob = new Blob([typeof data === 'string' ? data : JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

// ── dashboard-level date filter → query context ──
// Superset takes the range in two places: `time_range` on the query and
// the `val` of every TEMPORAL_RANGE filter the chart was saved with. A
// chart with no temporal filter is left alone — there is no column to
// apply the range to, so forcing one would error.
export function applyTimeRangeToQueryContext(queryContext, timeRange) {
  if (!queryContext || !timeRange) return queryContext;

  return {
    ...queryContext,
    queries: (queryContext.queries || []).map(query => ({
      ...query,
      time_range: timeRange,
      filters: (query.filters || []).map(filter => (filter?.op === 'TEMPORAL_RANGE' ? { ...filter, val: timeRange } : filter)),
    })),
  };
}
